import React from 'react';

import { Form, Input, Modal } from 'antd';
import { useFormik } from 'formik';

import { MODAL_WIDTH } from './AddWebhookModal';
import { useUpsertErrorNotif } from '../api/upsertErrorNotif';
import { buildErrorNotifSchema } from '../schemas/errorNotifSchema';
import { IErrorNotif } from '../types';
import CancelSubmitFooterModal from '@/components/CancelSubmitFooterModal';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

interface IErrorNotifModalProps {
  partnerId: string;
  notif: IErrorNotif | null;
  open: boolean;
  onClose: () => void;
}

interface IErrorNotifFormValues {
  email: string;
}

export const ErrorNotifModal = ({
  partnerId,
  notif,
  open,
  onClose,
}: IErrorNotifModalProps) => {
  const addToast = useToasts((state) => state.addItem);
  const isEdit = !!notif;

  const { mutateAsync: upsertNotif, isPending } = useUpsertErrorNotif({
    partnerId,
  });

  const formik = useFormik<IErrorNotifFormValues>({
    initialValues: { email: notif?.email ?? '' },
    validationSchema: buildErrorNotifSchema(),
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await upsertNotif({ errNotifId: notif?.notif_id, values });
        addToast(
          {
            msg: isEdit ? 'Email updated' : 'Email added',
            type: MessageType.Success,
          },
          'root',
          {}
        );
        formik.resetForm();
        onClose();
      } catch (err) {
        addToast(
          {
            msg: err instanceof Error ? err.message : 'Operation failed',
            type: MessageType.Error,
          },
          'root',
          {}
        );
      }
    },
  });

  const isInvalid = !formik.values.email || !!formik.errors.email;

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal
      title={isEdit ? 'Edit notification email' : 'Add notification email'}
      open={open}
      destroyOnHidden
      footer={CancelSubmitFooterModal(
        isInvalid,
        formik.handleSubmit,
        handleCancel,
        isPending
      )}
      onCancel={handleCancel}
      width={MODAL_WIDTH}
    >
      <Form layout="vertical">
        <Form.Item
          label="Email:"
          validateStatus={
            formik.touched.email && formik.errors.email ? 'error' : ''
          }
          help={
            formik.touched.email && formik.errors.email
              ? formik.errors.email
              : ''
          }
        >
          <Input
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="email@example.com"
            data-testid="error-notif-email-input"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
